import { useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { Link } from "react-router";

import { SelectLanguage } from "features/select-language";
import { SelectTimeZone } from "features/select-time-zone/select-time-zone";
import { Description, Layout } from "pages/ui/page";
import { useAddUserPreferencesMutation } from "shared/api/use-add-user-preferences-mutation";
import { useDeleteUserPreferencesMutation } from "shared/api/use-delete-user-preferences-mutation";
import { useEditUserPreferencesMutation } from "shared/api/use-edit-user-preferences-mutation";
import { useUpdatePreferencesSettingsMutation } from "shared/api/use-update-settings-mutation";
import { Breadcrumbs } from "shared/ui/breadcrumbs";
import { Button } from "shared/ui/button";
import { Panel } from "shared/ui/panel";
import { Tab, Tabs } from "shared/ui/tabs";

import headerImageSrc from "./assets/eggs.png";
import { PersistentContextSheet } from "./persistent-context-sheet";

type Tab = "context" | "examples";

const examples = [
  "My fullname is: [your name]. I live in [city]",
  "My husband/wife is in my contacts as [contact name]",
  "I have two pets: [describe them], they are on a special diet [more useful context here]",
  "Be concise and use emojis when appropriate",
  "I am a vegeterian",
];

type Props = {
  knowledges: string[];
  timezone: string | undefined;
  language: string | undefined;
};

export const Content = (props: Props) => {
  const { knowledges, language, timezone } = props;
  const [languageCode, setLanguageCode] = useState(language ?? "en");
  const [tzCode, setTzCode] = useState(timezone ?? "");
  const [tab, setTab] = useState<Tab>("context");
  const [isOpen, setIsOpen] = useState(false);
  const [knowledgeIdx, setKnowledgeIdx] = useState<number>();

  const deletePreferenceMutation = useDeleteUserPreferencesMutation();
  const addPreferenceMutation = useAddUserPreferencesMutation();
  const editPreferenceMutation = useEditUserPreferencesMutation();

  const settingsMutation = useUpdatePreferencesSettingsMutation();

  const items = (tab === "context" ? knowledges : examples) ?? [];

  const handleItemClick = (index: number) => {
    setKnowledgeIdx(index);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setKnowledgeIdx(undefined);
    }, 500);
  };

  const handleSave = async (value: string) => {
    try {
      if (typeof knowledgeIdx !== "number" || tab === "examples") {
        await addPreferenceMutation.mutateAsync({ value });
      } else {
        await editPreferenceMutation.mutateAsync({
          value,
          index: knowledgeIdx,
        });
      }
      setTab("context");
    } catch (error) {
      console.error(error);
    }

    setKnowledgeIdx(undefined);
    setIsOpen(false);
  };
  const handleDelete = async () => {
    if (typeof knowledgeIdx === "undefined") return;
    try {
      await deletePreferenceMutation.mutateAsync({ index: knowledgeIdx });
      setIsOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleTimezoneUpdate = async (tzCode: string) => {
    try {
      setTzCode(tzCode);
      await settingsMutation.mutateAsync({ timezone: tzCode });
    } catch (error) {
      console.error(error);
    }
  };

  const handleLanguageUpdate = async (code: string) => {
    try {
      setLanguageCode(code);
      await settingsMutation.mutateAsync({ language: code });
    } catch (error) {
      console.error(error);
    }
  };

  const knowledge =
    typeof knowledgeIdx === "number" ? items[knowledgeIdx] : undefined;
  console.log("knowledge", knowledge);

  return (
    <Layout headerImgSrc={headerImageSrc}>
      <Breadcrumbs>
        <Breadcrumbs.Item as={Link} to="/settings">
          Settings
        </Breadcrumbs.Item>
        <Breadcrumbs.Item isActive>Preferences</Breadcrumbs.Item>
      </Breadcrumbs>

      <div className="mt-3 flex flex-col gap-y-2">
        <SelectLanguage code={languageCode} onSelect={handleLanguageUpdate} />
        <SelectTimeZone tzCode={tzCode} onSelect={handleTimezoneUpdate} />
      </div>

      <main className="mt-5 flex flex-col">
        <header className="flex items-center">
          <div className="flex flex-col gap-2">
            <h4 className="text-lg leading-5.5 font-medium tracking-[0.4px] text-white">
              Persistent Context
            </h4>
            <Description>context for the assistant</Description>
          </div>
          <Button
            className="ml-auto"
            size="34"
            theme="primary"
            onClick={() => {
              setIsOpen(true);
            }}
          >
            <FaPlus className="mr-1.5 size-3.5" /> Add new item
          </Button>
        </header>

        <Tabs className="mt-3">
          <Tab isActive={tab === "context"} onClick={() => setTab("context")}>
            My Persistent Context
          </Tab>
          <Tab isActive={tab === "examples"} onClick={() => setTab("examples")}>
            Examples
          </Tab>
        </Tabs>

        <div className="mt-5 flex flex-col gap-y-2">
          {items.map((item, index) => {
            return (
              <Panel key={index} onClick={() => handleItemClick(index)}>
                {item}
              </Panel>
            );
          })}
        </div>
      </main>

      {isOpen && (
        <PersistentContextSheet
          isOpen={isOpen}
          initialValue={knowledge ?? ""}
          title={
            typeof knowledgeIdx === "number"
              ? `Persistent Context #${knowledgeIdx + 1}`
              : "New Persistent Context"
          }
          onSave={handleSave}
          onClose={handleClose}
          onDelete={typeof knowledgeIdx === "number" ? handleDelete : undefined}
        />
      )}
    </Layout>
  );
};
